import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { discussionService, PostCreateData } from '../../api/discussionService';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';

interface CreatePostFormProps {
  onSuccess: () => void;
  onCancel: () => void;
}

const FormContainer = styled.div`
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 20px;
  margin-bottom: 30px;
`;

const FormTitle = styled.h2`
  font-size: 1.5rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 20px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  border: 1px solid ${props => props.theme.colors.bgDark};
  border-radius: 4px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 10px;
  border: 1px solid ${props => props.theme.colors.bgDark};
  border-radius: 4px;
  font-size: 1rem;
  min-height: 150px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 10px;
  border: 1px solid ${props => props.theme.colors.bgDark};
  border-radius: 4px;
  font-size: 1rem;
  background-color: #fff;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 10px;
`;

const Tag = styled.div`
  background-color: ${props => props.theme.colors.bgLight};
  color: ${props => props.theme.colors.text};
  padding: 5px 10px;
  border-radius: 15px;
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.9rem;
`;

const RemoveTagButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1.1rem;
  color: ${props => props.theme.colors.danger};
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: ${props => props.theme.colors.dangerDark};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 10px 20px;
  border-radius: 4px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  background-color: ${props => 
    props.variant === 'primary' ? props.theme.colors.primary : '#fff'};
  color: ${props => 
    props.variant === 'primary' ? '#fff' : props.theme.colors.text};
  border: 1px solid ${props => 
    props.variant === 'primary' ? props.theme.colors.primary : props.theme.colors.bgDark};
  
  &:hover {
    background-color: ${props => 
      props.variant === 'primary' ? props.theme.colors.primaryDark : props.theme.colors.bgLight};
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: ${props => props.theme.colors.danger};
  font-size: 0.9rem;
  margin-top: 10px;
`;

const CreatePostForm: React.FC<CreatePostFormProps> = ({ onSuccess, onCancel }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<PostCreateData>({
    title: '',
    content: '',
    category: 'general',
    tags: []
  });
  const [tagInput, setTagInput] = useState('');
  const [error, setError] = useState('');
  
  const mutation = useMutation({
    mutationFn: (postData: PostCreateData) => discussionService.createPost(postData),
    onSuccess: () => {
      onSuccess();
    },
    onError: (error: any) => {
      console.error('Post creation error:', error);
      
      // Handle authentication errors
      if (error.response?.status === 401) {
        setError('You must be logged in to create a post. Please log in and try again.');
        // Optional: Redirect to login page after a delay
        setTimeout(() => {
          navigate('/login', { state: { from: '/community' } });
        }, 3000);
        return;
      }
      
      setError(error.response?.data?.message || 'Failed to create post. Please try again.');
    }
  });
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleAddTag = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      if (!formData.tags?.includes(tagInput.trim())) {
        setFormData(prev => ({
          ...prev,
          tags: [...(prev.tags || []), tagInput.trim()]
        }));
      }
      setTagInput('');
    }
  };
  
  const handleRemoveTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags?.filter(tag => tag !== tagToRemove)
    }));
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    if (!formData.title.trim()) {
      setError('Title is required');
      return;
    }
    
    if (!formData.content.trim()) {
      setError('Content is required');
      return;
    }
    
    mutation.mutate(formData);
  };
  
  return (
    <FormContainer>
      <FormTitle>Create New Post</FormTitle>
      <form onSubmit={handleSubmit}>
        <FormGroup>
          <Label htmlFor="title">Title</Label>
          <Input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleChange}
            placeholder="Enter a descriptive title"
            required
          />
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="content">Content</Label>
          <TextArea
            id="content"
            name="content"
            value={formData.content}
            onChange={handleChange}
            placeholder="Write your post content here..."
            required
          />
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="category">Category</Label>
          <Select
            id="category"
            name="category"
            value={formData.category}
            onChange={handleChange}
          >
            <option value="general">General</option>
            <option value="questions">Questions</option>
            <option value="testimonials">Testimonials</option>
            <option value="resources">Resources</option>
          </Select>
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="tags">Tags</Label>
          <Input
            type="text"
            id="tags"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={handleAddTag}
            placeholder="Type a tag and press Enter"
          />
          <TagsContainer>
            {formData.tags?.map(tag => (
              <Tag key={tag}>
                {tag}
                <RemoveTagButton
                  type="button"
                  onClick={() => handleRemoveTag(tag)}
                  aria-label="Remove tag"
                >
                  &times;
                </RemoveTagButton>
              </Tag>
            ))}
          </TagsContainer>
        </FormGroup>
        
        {error && <ErrorMessage>{error}</ErrorMessage>}
        
        <ButtonGroup>
          <Button type="button" onClick={onCancel}>
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="primary"
            disabled={mutation.isPending}
          >
            {mutation.isPending ? 'Creating...' : 'Create Post'}
          </Button>
        </ButtonGroup>
      </form>
    </FormContainer>
  );
};

export default CreatePostForm; 